import React, { useEffect, useState } from 'react';
import { compose, withHandlers, withPropsOnChange, withState } from 'recompose';
import { ObjectShim } from '@packages/helpers/core/shims/object-shim';
import { evaluateTextContentByScenarios } from '../../helpers/workers/evaluate-text-content-by-scenarios';
import { changeLastPartOfPath, createCustomPageProps } from '../../helpers/utils';
import { usePrevious } from '../../helpers/hooks/use-previous';
import { UserService } from '../../services/user-service';
import { CUSTOM_PAGE_TYPE, CUSTOM_PAGE_TYPE_TO_CONFIG } from '../../helpers/constants';
import { isModuleCompletedPlain } from '../../helpers/custom-page/is-completed';
import { ConfigService } from '../../services/config-service';
import { withCancellableHandlers } from './with-cancellable-handlers';

const { START_PAGE, RESULT_PAGE, INFO_PAGE } = CUSTOM_PAGE_TYPE;

const lifecycle = Component => props => {
  const {
    getPage,
    yieldPage,
    processConfig,
    currentPage: { slug, type, statusMapping },
    location: { pathname },
    statuses,
    userAttributes,
    plainStatuses
  } = props;

  const [loading, setLoading] = useState(true);

  const prevPathname = usePrevious(pathname);
  const prevSlug = usePrevious(slug);
  const prevUserAttributes = usePrevious(userAttributes);
  const prevStatuses = usePrevious(plainStatuses);

  // TODO: Refactor invoke
  useEffect(() => {
    const invoke = async () => {
      if (pathname !== prevPathname || slug !== prevSlug) {
        setLoading(true);

        if (
          pathname !== prevPathname &&
          // prevent from requesting the start page when activity completed
          !(type === START_PAGE && isModuleCompletedPlain(statuses[statusMapping.activity]))
        ) {
          // Fetch the page from the WP
          await getPage(pathname);

          // merge configuration (mainly page header) from WP and CONFIG after page request
          await processConfig();
        }

        if (slug !== prevSlug) {
          await yieldPage(slug);
        }

        setLoading(false);
      }
    };

    invoke();
  }, [getPage, yieldPage, slug, pathname, prevSlug, prevPathname, statuses, statusMapping, processConfig, type]);

  useEffect(() => {
    const triggerConfigRecalc = async () => {
      if (prevUserAttributes !== userAttributes || prevStatuses !== plainStatuses) {
        // Recalculate config subheading when updating wheel inputs on page
        await processConfig();
      }
    };

    triggerConfigRecalc();
  }, [userAttributes, plainStatuses, prevUserAttributes, prevStatuses, processConfig]);

  return <Component loading={loading} {...props} />;
};

export const withContainer = compose(
  withState('configContent', 'setConfigContent', {}),
  withHandlers({
    processEditButton:
      ({ location: { pathname }, setEditButton }) =>
      ({ url, editButtonSkipQuestions }) => {
        setEditButton(pathname, url + (editButtonSkipQuestions ? '' : '?mode=edit'));
      },
    processHelpButton:
      ({ location: { pathname }, toggleModal, getFAQ, setHelpButton }) =>
      async ({ url, type = [], modelIndex = 0, model }) => {
        if (type[0] === 'Model') {
          const callback = () => toggleModal(true, 'help', { faqName: model, faqIndex: modelIndex });

          await getFAQ({ name: model });
          setHelpButton(pathname, '', callback);
        } else {
          setHelpButton(pathname, url);
        }
      }
  }),
  withCancellableHandlers({
    evaluateData:
      ({ userAttributes, currentPage, plainStatuses, pageName }) =>
      async () => {
        const headerScenarios = ConfigService.get(
          `${CUSTOM_PAGE_TYPE_TO_CONFIG[currentPage.type]}.pageHeader.${pageName}`,
          {}
        );

        return await evaluateTextContentByScenarios({
          userState: { userAttributes, activity: plainStatuses },
          scenarios: headerScenarios,
          fieldNames: ['subHeading']
        });
      }
  }),
  withPropsOnChange(['currentPage', 'configContent'], ({ currentPage, configContent }) => {
    return {
      // Merging evaluated content from CONFIG with WP content.
      currentPage: { ...ObjectShim.mergeDeep(currentPage, { content: configContent }) }
    };
  }),
  withHandlers({
    getPage:
      ({ getCustomPage }) =>
      pathname => {
        return getCustomPage(createCustomPageProps(pathname));
      },
    yieldPage:
      ({
        currentPage,
        history,
        location: { pathname },
        statuses,
        sendStatus,
        trustCard: stateTrustCard,
        getFAQ,
        processEditButton,
        processHelpButton,
        editButtonHeader,
        helpButtonHeader,
        getMarketplacePartners
      }) =>
      async slug => {
        if (!UserService.hasAccount() && slug) {
          const { statusMapping, statusRedirect, editButton, helpButton, faq, type, trustCard } = currentPage;

          if (type !== INFO_PAGE) {
            if (isModuleCompletedPlain(statuses[statusMapping.activity]) && type === START_PAGE) {
              return history.replace(statusRedirect.isCompleteUrl);
            }

            if (!isModuleCompletedPlain(statuses[statusMapping.activity]) && type === RESULT_PAGE) {
              return history.replace(changeLastPartOfPath(pathname, '/start'));
            }
          }

          if (editButton.display && !editButtonHeader) {
            processEditButton(editButton);
          }

          if (helpButton.display && !helpButtonHeader) {
            await processHelpButton(helpButton);
          }

          if (faq.display && faq.name) {
            await getFAQ({ name: faq.name });
          }

          if (trustCard.display && !stateTrustCard) {
            await getMarketplacePartners();
          }

          if (type === START_PAGE) {
            const { activity, status } = statusMapping;

            await sendStatus(activity, status);
          }
        }
      },
    processConfig:
      ({ evaluateData, setConfigContent }) =>
      async () => {
        const value = await evaluateData();

        setConfigContent(value);
      }
  }),
  lifecycle
);
